import React, { useMemo } from 'react'
import config from 'constants/config'
import PriceRowTaxesAndFees from 'components/Luxkit/PricePoints/PriceRowTaxesAndFees'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import AdditionalGuestsPopup from 'components/Common/AdditionalGuestsPopup/AdditionalGuestsPopup'
import { OfferPackageOptionView, arePricesLoading, isBNBLAvailable, getFromDurationLabel, getCheapestAvailablePackageOption } from 'selectors/offerPage/offerPageSelectors'
import { includedGuestsExceededItems } from 'lib/checkout/cartReservationUtils'
import TextButton from 'components/Luxkit/Button/TextButton'
import { MINIMUM_DISCOUNT_TO_SHOW_PERCENTAGE_BADGE } from 'constants/content'
import Caption from 'components/Luxkit/Typography/Caption'
import PriceRowPriceCaption from 'components/Luxkit/PricePoints/PriceRowPriceCaption'
import PriceRowValueDiscountWithCaption from 'components/Luxkit/PricePoints/Value/PriceRowValueDiscountWithCaption'
import { getOptimizelyExperimentVariation } from 'lib/optimizely/optimizelyUtils'
import { OptimizelyExperiments } from 'constants/optimizely'
import { getPackageViewPricingPerNight } from 'lib/offer/getPackageViewPricingPerNightUtils'
import LuxPlusPriceStack from 'luxPlus/components/LuxPlusPriceStack'
import OfferPriceDetailsRow from 'components/Common/PriceDetails/OfferPriceDetailsRow'
import LuxLoyaltyPoints from 'luxLoyalty/components/LuxLoyaltyPoints'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import WalledContent from 'components/Common/WalledContent'
import { generateLuxLoyaltyPointsCalculatorHotelOptions, generateLuxLoyaltyPointsCalculatorBundledFlightOptions } from 'luxLoyalty/lib/pointsCalculation/calculatorOptionsGenerators'
import Group from 'components/utils/Group'
import PriceRowValueDiscount from 'components/Luxkit/PricePoints/Value/PriceRowValueDiscount'
import AccommodationPackageTilePricingBlocks from 'components/OfferPage/Accommodation/AccommodationPackageTilePricingBlocks'
import BodyTextBlock from 'components/Luxkit/TextBlocks/BodyTextBlock'
import LinePlaneDepartureIcon from 'components/Luxkit/Icons/line/LinePlaneDepartureIcon'
import UrgencyLabel from 'components/Luxkit/Label/UrgencyLabel'
import SolidFireIcon from 'components/Luxkit/Icons/solid/SolidFireIcon'
import useFlightDeals from 'hooks/Flights/useFlightDeals'
import useOptimizelyExperiment, { OptimizelyExperimentVariantType, OptimizelyExperimentVariantTypeEnum } from 'hooks/Optimizely/useOptimizelyExperiment'
import LuxPlusLabelWithModal from 'luxPlus/components/LuxPlusLabelWithModal'
import { getInclusionsViewableLuxPlusTier } from 'selectors/luxPlusSelectors'

interface Props {
  offer: App.Offer;
  rooms: Array<App.Occupants>;
  cheapestOption: OfferPackageOptionView;
  durationLabel: string;
  isPerNightPricingTestEnabled: boolean;
  flightOrigin?: App.Airport;
  airports: Array<App.Airport>;
  showHotelPriceWithFlights: boolean;
  inclusionsViewableLuxPlusTier: App.MembershipTier;
}

function FlashOfferIntroPricingBlock(props: Props) {
  const {
    offer,
    cheapestOption: packageView,
    rooms,
    durationLabel,
    isPerNightPricingTestEnabled,
    flightOrigin,
    airports,
    showHotelPriceWithFlights,
    inclusionsViewableLuxPlusTier,
  } = props

  const selectedPackageView = useMemo(() => isPerNightPricingTestEnabled ? getPackageViewPricingPerNight(packageView) : packageView, [packageView, isPerNightPricingTestEnabled])
  const {
    flightPrice,
    hotelValue,
    hotelPrice,
    hotelMemberPrice,
    package: pkg,
    hotelDiscountPercent,
    bundledDiscountPercent,
    propertyFees,
    taxesAndFees,
  } = selectedPackageView

  const totalPrice = useMemo(() => {
    let total = hotelPrice + (propertyFees ?? 0)
    if (offer.bundledWithFlightsOnly) {
      total += flightPrice
    }
    return total
  }, [flightPrice, hotelPrice, offer.bundledWithFlightsOnly, propertyFees])

  const totalMemberPrice = useMemo(() => {
    let total = hotelMemberPrice
    if (hotelMemberPrice > 0) {
      total = hotelMemberPrice + (propertyFees ?? 0)
      if (offer.bundledWithFlightsOnly) {
        total += flightPrice
      }
    }
    return total
  }, [flightPrice, hotelMemberPrice, offer.bundledWithFlightsOnly, propertyFees])

  const showIncludedGuestsExceeded = useMemo(() => !pkg.roomRate?.extraGuestSurcharges.length && includedGuestsExceededItems(rooms || [], pkg, offer), [pkg, rooms, offer])
  const shouldShowDiscountPercent = hotelDiscountPercent >= MINIMUM_DISCOUNT_TO_SHOW_PERCENTAGE_BADGE
  const shouldShowBundledDiscountPercent = bundledDiscountPercent >= MINIMUM_DISCOUNT_TO_SHOW_PERCENTAGE_BADGE
  const showDiscountPill = !offer?.isDiscountPillHidden && (shouldShowDiscountPercent && !offer.bundledWithFlightsOnly) || (offer.bundledWithFlightsOnly && shouldShowBundledDiscountPercent)

  const showPriceDetails = taxesAndFees > 0 || (propertyFees ?? 0) > 0 || config.agentHub.isEnabled
  const shouldShowValue = hotelValue > hotelPrice && pkg.shouldDisplayValue
  const saleUnit = useMemo(() => {
    if (offer.bundledWithFlightsOnly) return isPerNightPricingTestEnabled ? 'night per package*' : 'package*'
    return isPerNightPricingTestEnabled ? 'night' : 'total'
  }, [isPerNightPricingTestEnabled, offer.bundledWithFlightsOnly])

  const pointsEarnCalculationRequests = useMemo<Array<App.LuxLoyaltyPointsEarnCalculationRequest | undefined>>(() => [
    generateLuxLoyaltyPointsCalculatorHotelOptions(offer, { packageView: selectedPackageView }),
    generateLuxLoyaltyPointsCalculatorBundledFlightOptions(offer, {
      packageView: selectedPackageView,
      skip: !offer.bundledWithFlightsOnly,
    }),
  ], [offer, selectedPackageView])

  const flightDestination = useMemo(() => airports.find(({ code }) => code === offer.flightDestinationPort), [airports, offer.flightDestinationPort])
  const [deals] = useFlightDeals('all', {
    originAirportCode: flightOrigin?.code,
    destinationAirportCode: flightDestination?.code,
  })
  const showFlightsDealTag = flightOrigin && flightDestination && deals.length > 0
  const croPricingBlocksStickerVsPriceVariant = useOptimizelyExperiment(OptimizelyExperiments.croPricingBlocksEmphasisOnStickerVsValuedUpToPrice) as OptimizelyExperimentVariantType
  const showMemberPrice = !!totalMemberPrice && totalMemberPrice > 0
  const saveAmount = showMemberPrice ? (packageView.basePrice ?? 0) - totalMemberPrice : undefined
  const luxPlusInclusions = offer.lowestPricePackage?.luxPlusInclusionsByTier?.[inclusionsViewableLuxPlusTier as App.MembershipSubscriptionTier]
  const LuxPlusLabel = useMemo(() => {
    if (croPricingBlocksStickerVsPriceVariant !== OptimizelyExperimentVariantTypeEnum.variant_1) return undefined
    if (!(saveAmount) || offer.luxPlus.access === 'memberOnly') return undefined
    return (
      <LuxPlusLabelWithModal
        type="member-price-offer"
        offer={offer}
        saveAmount={saveAmount}
        hideSaveAmountPlus
        inclusions={luxPlusInclusions}
      />
    )
  }, [croPricingBlocksStickerVsPriceVariant, saveAmount, luxPlusInclusions, offer])
  if (showHotelPriceWithFlights) {
    return <WalledContent enforced={offer.walledGarden}>
      <Group direction="vertical" gap={12} horizontalAlign="center">
        <Group direction="vertical" fullWidth gap={12}>
          {showDiscountPill && <PriceRowValueDiscount type="estimate" discountPercentage={hotelDiscountPercent} />}
          {LuxPlusLabel}
          <AccommodationPackageTilePricingBlocks>
            <VerticalSpacer gap={12}>
              <BodyTextBlock variant="large" weight="bold">Hotel</BodyTextBlock>
              <VerticalSpacer gap={8}>
                <div>
                  <PriceRowPriceCaption>{durationLabel}</PriceRowPriceCaption>
                  <LuxPlusPriceStack
                    memberPrice={totalMemberPrice}
                    price={totalPrice}
                    size="L"
                    saleUnit={saleUnit}
                    offerConfig={offer.luxPlus}
                    pricingBlockOptimizelyExperimentVariant={croPricingBlocksStickerVsPriceVariant}
                  />
                  {showIncludedGuestsExceeded && (
                    <AdditionalGuestsPopup
                      complex={false}
                      description="+ Extra guest surcharge"
                      modalContent={pkg.roomPolicyDescription}
                    />
                  )}
                  {shouldShowValue && <PriceRowValueDiscountWithCaption
                    data-testid="value-box"
                    size="L"
                    originalValue={hotelValue}
                  />}
                </div>
              </VerticalSpacer>
            </VerticalSpacer>
            <VerticalSpacer gap={12}>
              <BodyTextBlock startIcon={<LinePlaneDepartureIcon />} variant="large" weight="bold">Hotel + flights</BodyTextBlock>
              <VerticalSpacer gap={8}>
                <div>
                  <PriceRowPriceCaption>{durationLabel}</PriceRowPriceCaption>
                  <LuxPlusPriceStack
                    memberPrice={totalMemberPrice > 0 ? totalMemberPrice + flightPrice : 0}
                    price={totalPrice + flightPrice}
                    size="L"
                    saleUnit={saleUnit}
                    offerConfig={offer.luxPlus}
                    pricingBlockOptimizelyExperimentVariant={croPricingBlocksStickerVsPriceVariant}
                  />
                  {showIncludedGuestsExceeded && (
                    <AdditionalGuestsPopup
                      complex={false}
                      description="+ Extra guest surcharge"
                      modalContent={pkg.roomPolicyDescription}
                    />
                  )}
                  {shouldShowValue && <PriceRowValueDiscountWithCaption
                    data-testid="value-box"
                    size="L"
                    originalValue={hotelValue + flightPrice}
                  />}
                </div>
                {showFlightsDealTag && <UrgencyLabel variant="critical1" icon={<SolidFireIcon />}>Flight deal available</UrgencyLabel>}
              </VerticalSpacer>
            </VerticalSpacer>
          </AccommodationPackageTilePricingBlocks>
        </Group>
        <LuxLoyaltyPoints calculationRequests={pointsEarnCalculationRequests} calculationType="estimate" />
        <PriceRowTaxesAndFees
          endIcon={showPriceDetails && <OfferPriceDetailsRow
            offerPage
            offer={offer}
            duration={pkg.duration}
            propertyFees={packageView.propertyFees}
            taxesAndFees={packageView.taxesAndFees}
            hotelPrice={packageView.hotelPrice}
            hotelMemberPrice={packageView.hotelMemberPrice}
            showMemberPrice={packageView.hotelMemberPrice > 0}
            flightPrice={packageView.flightPrice}
            rooms={rooms}
            extraGuestMessage={showIncludedGuestsExceeded ? pkg.roomPolicyDescription : ''}
            dueAtPropertyMessage={offer.property?.taxesAndFeesContent ? offer.property.taxesAndFeesContent : ''}
            trigger="info-icon"
            cta={<TextButton kind="primary" fit="flex">View {config.brandConfig.travelSynonym} options</TextButton>}
          />}
        />
      </Group>
    </WalledContent>
  }

  return <WalledContent enforced={offer.walledGarden}>
    <VerticalSpacer gap={8}>
      <div>
        <PriceRowPriceCaption>{durationLabel}</PriceRowPriceCaption>
        <LuxPlusPriceStack
          memberPrice={totalMemberPrice}
          price={totalPrice}
          size="L"
          saleUnit={saleUnit}
          offerConfig={offer.luxPlus}
        />
        {offer.bundledWithFlightsOnly && !rooms && <Caption variant="medium" colour="neutral-three">
        &nbsp;Two Adults
        </Caption>}
        {showIncludedGuestsExceeded && (
          <AdditionalGuestsPopup
            complex={false}
            description="+ Extra guest surcharge"
            modalContent={pkg.roomPolicyDescription}
          />
        )}
        {shouldShowValue && <PriceRowValueDiscountWithCaption
          data-testid="value-box"
          size="L"
          originalValue={offer.bundledWithFlightsOnly ? hotelValue + flightPrice : hotelValue}
          discountPercentage={showDiscountPill ? offer.bundledWithFlightsOnly ? bundledDiscountPercent : hotelDiscountPercent : undefined}
        />}
        <PriceRowTaxesAndFees
          endIcon={showPriceDetails && <OfferPriceDetailsRow
            offerPage
            offer={offer}
            duration={pkg.duration}
            propertyFees={packageView.propertyFees}
            taxesAndFees={packageView.taxesAndFees}
            hotelPrice={packageView.hotelPrice}
            hotelMemberPrice={packageView.hotelMemberPrice}
            showMemberPrice={packageView.hotelMemberPrice > 0}
            flightPrice={offer.bundledWithFlightsOnly ? packageView.flightPrice : undefined}
            rooms={rooms}
            extraGuestMessage={showIncludedGuestsExceeded ? pkg.roomPolicyDescription : ''}
            dueAtPropertyMessage={offer.property?.taxesAndFeesContent ? offer.property.taxesAndFeesContent : ''}
            trigger="info-icon"
            cta={
              <TextButton
                kind="primary"
                fit="flex"
              >
                View {config.brandConfig.travelSynonym} options
              </TextButton>
          }
          />}
        />
      </div>
      <LuxLoyaltyPoints calculationRequests={pointsEarnCalculationRequests} calculationType="estimate" />
    </VerticalSpacer>
  </WalledContent>
}

export default withOfferPageAndReduxState((state, appState) => ({
  cheapestOption: getCheapestAvailablePackageOption(state, appState),
  rooms: state.rooms,
  offer: state.offer,
  flightOrigin: state.airport,
  calendarsLoading: arePricesLoading(appState, state),
  isBNBLAvailable: isBNBLAvailable(state),
  isSpoofed: appState.auth.account.isSpoofed,
  durationLabel: getFromDurationLabel(state, appState),
  isPerNightPricingTestEnabled: !!getOptimizelyExperimentVariation(appState, OptimizelyExperiments.pricePerNight),
  airports: appState.geo.airports,
  inclusionsViewableLuxPlusTier: getInclusionsViewableLuxPlusTier(appState),
}))(FlashOfferIntroPricingBlock)
